import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { useChat } from './hooks/useChat';
import { useDashboard } from './hooks/useDashboard';
import { useBranding } from './hooks/useBranding';
import { useTheme } from './hooks/useTheme';
import { useTenantFeatures } from './hooks/useTenantFeatures';
import { useDetailContext } from './hooks/useDetailContext';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { apiClient, ApiError } from './lib/apiClient';
import LoginForm from './components/LoginForm';
import OnboardingWizard from './components/OnboardingWizard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Impressum from './components/Impressum';
import CookieConsent from './components/CookieConsent';
import ContextMenu from './components/ContextMenu';
import InstallBanner from './components/InstallBanner';
import ProfilePage from './components/ProfilePage';
import SettingsPage from './components/SettingsPage';
import ChatHistoryPage from './components/ChatHistoryPage';
import SubscriptionPage from './components/SubscriptionPage';
import FarmOverviewPage from './components/FarmOverviewPage';
import ConsolePage from './components/ConsolePage';
import PlatformConsolePage from './components/PlatformConsolePage';
import EnterpriseSwitcher from './components/EnterpriseSwitcher';
import DetailPage from './components/DetailPage';
import AdaptiveLayout from './layouts/AdaptiveLayout';
import { isAlcedoConsoleDomain } from './utils/domain';
import ShareManagement from './components/ShareManagement';
import PublicShareView from './components/PublicShareView';
import ShareFlow from './components/ShareFlow';
import InspectorView from './components/InspectorView';
import UpsellModal from './components/UpsellModal';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import WalletPage from './components/credits/WalletPage';
import BrainDumpPage from './components/braindump/BrainDumpPage';
import StorageUsagePage from './components/braindump/StorageUsagePage';
import { InsufficientCreditsProvider } from './components/credits/InsufficientCreditsHandler';
import MemberManagement from './components/MemberManagement';
import DocumentManager from './components/DocumentManager';
import AccountMenu from './components/AccountMenu';
import NotificationPreferencesView from './views/profile/NotificationPreferencesView';
import InviteAcceptPage from './components/InviteAcceptPage';
import PublicSignupPage from './components/PublicSignupPage';
import MagicLinkLandingPage from './components/MagicLinkLandingPage';
import { parseFieldRoute } from './utils/fieldRoute';
import { useOfflineSync } from './hooks/useOfflineSync';
import { ConflictResolutionDrawer } from './components/conflict/ConflictResolutionDrawer';
import { useToast } from './components/overlay/Toast';
import QueueDepthPanel from './components/debug/QueueDepthPanel';
import CommandPalette from './components/CommandPalette';
import useCommandPalette from './hooks/useCommandPalette';
import { NavigationProvider, useNavigationProfile } from './context/NavigationContext';

/** Decode JWT payload without verification (client-side only for UX purposes). */
function _parseJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

const API = import.meta.env.VITE_API_URL ?? '';

export default function App() {
  const { t } = useTranslation();
  const auth = useAuth();
  const { user, token, loading, error, login, register, logout } = auth;
  const { messages, sending, sendMessage, sendFile, newSession, loadSession } = useChat(token);
  const dashboard = useDashboard(token);
  const { branding } = useBranding(token);
  const { isEnabled: isFeatureEnabled } = useTenantFeatures(token);
  const { theme, setTheme, isDark, toggle: toggleTheme, resetToSystem } = useTheme();
  const { detailType, detailParams, openDetail, clearDetail } = useDetailContext();
  const { canInstall, promptInstall } = useInstallPrompt();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [page, setPage] = useState(window.location.pathname);
  const [settingsView, setSettingsView] = useState(null);
  const [userOverride, setUserOverride] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [seasonalContext, setSeasonalContext] = useState(null);
  const [enterpriseContext, setEnterpriseContext] = useState({ enterprise: null, betrieb: null });
  const [showEnterpriseSwitcherSheet, setShowEnterpriseSwitcherSheet] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState(null); // {limitType, usage}

  // A3-3: Command Palette (Cmd+K / Ctrl+K)
  const { open: cmdPaletteOpen, setOpen: setCmdPaletteOpen } = useCommandPalette();

  // A6 Sub-Phase 5: boot offline sync engine + conflict notification
  const { toast: syncToast } = useToast();
  const { activeConflict, dismissConflict } = useOfflineSync({ enabled: !!token });
  const [conflictDrawerOpen, setConflictDrawerOpen] = useState(false);
  useEffect(() => {
    if (activeConflict) {
      setConflictDrawerOpen(true);
      try {
        syncToast({
          variant: 'warning',
          title: t('conflict.conflictNotice', { defaultValue: 'Ein Änderungskonflikt erfordert deine Entscheidung' }),
          action: {
            label: t('conflict.reviewAction', { defaultValue: 'Prüfen' }),
            altText: t('conflict.reviewAction', { defaultValue: 'Prüfen' }),
            onClick: () => setConflictDrawerOpen(true),
          },
        });
      } catch { /* toast optional */ }
    }
  }, [activeConflict]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect impersonation session from JWT
  const jwtPayload = token ? _parseJwtPayload(token) : null;
  const isImpersonating = !!(jwtPayload?.impersonation_session);

  const activeUser = userOverride || user;

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setPage(path);
  };

  // Listen for popstate
  useEffect(() => {
    const handler = () => setPage(window.location.pathname);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // A15-N7: Sync /wallet URL → settingsView
  useEffect(() => {
    if (page === '/wallet' && token && activeUser) {
      setSettingsView('wallet');
    }
  }, [page, token, activeUser]);

  // G.23: Sync /fields/{id}/{tab} URL to detail context
  useEffect(() => {
    const route = parseFieldRoute(page);
    if (route.isFieldRoute && !route.listOnly && !route.invalidId) {
      openDetail('fields', { fieldId: route.fieldId, tab: route.tab });
    } else if (route.isFieldRoute && (route.listOnly || route.invalidId)) {
      // On /fields or invalid ID, ensure detail is cleared
      if (detailType === 'fields') clearDetail();
    } else if (detailType === 'fields') {
      // Navigated away from /fields/* — clear field detail
      clearDetail();
    }
  // Intentional: only react to page/route changes, not to detailType/clearDetail identity changes
  // which would cause a loop (clearDetail sets detailType which is checked inside).
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape key closes the detail overlay (works on all viewports, not just desktop)
  useEffect(() => {
    if (!detailType) return;
    const handler = (e) => {
      if (e.key === 'Escape') clearDetail();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [detailType, clearDetail]);

  // Sync language change to backend
  const handleLanguageChange = useCallback(async (langCode) => {
    if (!token) return;
    try {
      await apiClient.patch('/api/v1/aif/auth/language', { language: langCode }, { entityType: 'user', queueOnOffline: true });
    } catch { /* best-effort */ }
  }, [token]);

  const handleUserUpdate = useCallback((data) => {
    setUserOverride(data);
    dashboard.refresh();
  }, [dashboard.refresh]);

  // Fetch unread notification count + poll every 60s
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiClient.get('/api/v1/aif/notifications/unread-count');
      setUnreadCount(data?.unread_count || 0);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [token, fetchUnreadCount]);

  // Fetch seasonal context
  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/context/seasonal', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSeasonalContext(d); }).catch(() => {});
  }, [token]);

  // Watch for data_changed flag in chat messages → refresh dashboard
  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (messages.length > prevMsgCount.current) {
      const newMsgs = messages.slice(prevMsgCount.current);
      const hasDataChange = newMsgs.some(m => m.data_changed);
      if (hasDataChange) {
        dashboard.refresh();
        fetchUnreadCount();
      }
    }
    prevMsgCount.current = messages.length;
  }, [messages, dashboard.refresh, fetchUnreadCount]);

  // Check billing usage limits after sending messages
  useEffect(() => {
    if (!token || messages.length === 0) return;
    // Check every 5 messages
    if (messages.length % 5 !== 0) return;
    fetch(`${API}/api/v1/aif/billing/usage`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.messages && !data.messages.allowed) {
          setUpgradePrompt({ limitType: 'messages', usage: data.messages });
        }
      })
      .catch(() => {});
  }, [messages.length, token]);

  const handleShowDetail = useCallback((intent, params = {}) => {
    // Notizen tile tap → open settings overlay instead of detail sheet
    if (intent === 'brain-dump') {
      setSettingsView('brain-dump');
      return;
    }
    openDetail(intent, params);
  }, [openDetail]);

  const handleLoadSession = useCallback((sessionId, msgs) => {
    loadSession(sessionId, msgs);
  }, [loadSession]);

  // Account Menu wrapper — wraps TopBar avatar button with Radix Dropdown
  const AvatarMenuWrapper = useCallback(({ children }) => (
    <AccountMenu
      user={activeUser}
      onOpenSettings={(view) => setSettingsView(view)}
      onNavigate={navigate}
      onLogout={logout}
    >
      {children}
    </AccountMenu>
  ), [activeUser, navigate, logout]);

  // Public invite acceptance — accessible without login
  if (page.startsWith('/invite/')) {
    const inviteToken = page.slice(8);
    return <InviteAcceptPage inviteToken={inviteToken} />;
  }

  // Public inspector view — accessible without login
  if (page.startsWith('/inspect/')) {
    const inspectorToken = page.slice(9);
    return <InspectorView inspectorToken={inspectorToken} />;
  }

  // Public share view — accessible without login
  if (page.startsWith('/s/')) {
    const shareToken = page.slice(3);
    return <PublicShareView shareToken={shareToken} />;
  }

  // Public signup + magic-link pages — accessible without login
  if (page === '/signup' || page.startsWith('/signup?')) {
    return <PublicSignupPage />;
  }
  if (page === '/magic-link' || page.startsWith('/magic-link?')) {
    return <MagicLinkLandingPage />;
  }

  // Legal pages — accessible without login
  if (page === '/datenschutz') return <PrivacyPolicy onBack={() => navigate('/')} />;
  if (page === '/agb') return <TermsOfService onBack={() => navigate('/')} />;
  if (page === '/impressum') return <Impressum onBack={() => navigate('/')} />;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-page)]">
        <div className="w-12 h-12 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in
  if (!token || !activeUser) {
    return (
      <>
        <LoginForm
          onLogin={login}
          onRegister={async (form) => {
            await register(form);
            setShowOnboarding(true);
          }}
          error={error}
          branding={branding}
        />
        <CookieConsent token={null} />
      </>
    );
  }

  // Onboarding after registration
  if (showOnboarding) {
    return (
      <OnboardingWizard
        onComplete={() => setShowOnboarding(false)}
        onSkip={() => setShowOnboarding(false)}
      />
    );
  }

  const tier = activeUser.subscription_tier || 'free';
  const appName = branding?.app_name || t('app.name');

  const closeSettings = () => setSettingsView(null);

  return (
    <ErrorBoundary>
    <NavigationProvider token={token}>
    <InsufficientCreditsProvider>
    <>
      <OfflineBanner />
      {/* A3-2: Command Palette */}
      <CommandPalette
        open={cmdPaletteOpen}
        onOpenChange={setCmdPaletteOpen}
        navigate={navigate}
      />
      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-[var(--aif-z-sticky)] bg-amber-500 text-black text-center py-1.5 px-4 text-xs font-medium flex items-center justify-center gap-2">
          <span>👁️</span>
          <span>
            {t('impersonation.viewing')} <strong>{activeUser?.name || activeUser?.email || t('impersonation.customer')}</strong>.
            {t('impersonation.disabled')}
          </span>
          <button
            onClick={logout}
            className="ml-3 px-2 py-0.5 rounded bg-black/20 hover:bg-black/30 text-black text-[10px] font-semibold"
          >
            {t('impersonation.end')}
          </button>
        </div>
      )}

      <div
        className="h-[100dvh] flex flex-col bg-[var(--aif-bg-page)] overflow-hidden"
        style={{ paddingTop: isImpersonating ? '36px' : 'env(safe-area-inset-top, 0px)' }}
      >
        <AdaptiveLayout
          enterprise={enterpriseContext.enterprise}
          betrieb={enterpriseContext.betrieb}
          onOpenEnterpriseSwitcher={() => setShowEnterpriseSwitcherSheet(true)}
          onHamburger={() => setMenuOpen(true)}
          appName={appName}
          unreadCount={unreadCount}
          onBellClick={() => setShowNotifications(true)}
          onProfileClick={() => setSettingsView('profile')}
          onSearch={null}
          messages={messages}
          sending={sending}
          onSend={sendMessage}
          onSendFile={sendFile}
          onNewSession={newSession}
          onMenuOpen={() => setMenuOpen(true)}
          onShowDetail={handleShowDetail}
          seasonalContext={seasonalContext}
          token={token}
          dashboard={dashboard}
          user={activeUser}
          onUserUpdate={handleUserUpdate}
          onCardTap={handleShowDetail}
          isFeatureEnabled={isFeatureEnabled}
          openDetail={openDetail}
          detailType={detailType}
          detailParams={detailParams}
          sprayWindows={dashboard.sprayWindows}
          onOpenWallet={() => setSettingsView('wallet')}
          AvatarWrapper={AvatarMenuWrapper}
        />
      </div>

      {/* Detail Page — bottom sheet overlay.
          z-[var(--aif-z-detail-panel)] sits above MapView overlays and EnterpriseSwitcher.
          Backdrop click dismisses. Handle bar + rounded-t-2xl give bottom-sheet UX.
          animate-slideUp defined in tailwind config / global CSS. */}
      {detailType && (
        <div className="fixed inset-0 z-[var(--aif-z-detail-panel)]" aria-modal="true" data-testid="detail-overlay" data-detail-panel>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={clearDetail}
            data-backdrop
          />
          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 flex flex-col
                       bg-[var(--aif-bg-page)] rounded-t-2xl shadow-2xl
                       animate-slideUp overflow-hidden"
            style={{ height: '92vh' }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-0 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-[var(--aif-border-default)]" />
            </div>
            {/* Content */}
            <div className="flex-1 overflow-auto min-h-0">
              <DetailPage
                detailType={detailType}
                detailParams={detailParams}
                weather={dashboard.weather}
                subsidies={dashboard.subsidies}
                sprayWindows={dashboard.sprayWindows}
                fieldwork={dashboard.fieldwork}
                market={dashboard.market}
                finance={dashboard.finance}
                cashflow={dashboard.cashflow}
                token={token}
                goToChat={clearDetail}
                isDesktop={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* A6 Sub-Phase 7: Dev-only queue depth panel */}
      <QueueDepthPanel />

      {/* A6 Sub-Phase 5: Conflict Resolution Drawer (Tier 3) */}
      <ConflictResolutionDrawer
        open={conflictDrawerOpen}
        onOpenChange={(o) => { setConflictDrawerOpen(o); if (!o) dismissConflict(); }}
        conflict={activeConflict}
        onResolved={() => { setConflictDrawerOpen(false); dismissConflict(); }}
      />

      {/* Enterprise Switcher Sheet — controlled by TopBar button */}
      <EnterpriseSwitcher
        open={showEnterpriseSwitcherSheet}
        onOpenChange={setShowEnterpriseSwitcherSheet}
        token={token}
        onContextChange={setEnterpriseContext}
      />

      {/* Settings pages */}
      {settingsView === 'farm' && (
        <FarmOverviewPage onClose={closeSettings} token={token} user={activeUser} onUserUpdate={handleUserUpdate} />
      )}
      {settingsView === 'profile' && (
        <ProfilePage onClose={closeSettings} user={activeUser} token={token} onUserUpdate={handleUserUpdate} onOpenWallet={() => setSettingsView('wallet')} onOpenFarm={() => setSettingsView('farm')} />
      )}
      {settingsView === 'settings' && (
        <SettingsPage onClose={closeSettings} isDark={isDark} onToggleTheme={toggleTheme} theme={theme} onSetTheme={setTheme} onResetToSystem={resetToSystem} onLanguageChange={handleLanguageChange} />
      )}
      {settingsView === 'history' && (
        <ChatHistoryPage onClose={closeSettings} token={token} onLoadSession={handleLoadSession} />
      )}
      {settingsView === 'subscription' && (
        <SubscriptionPage onClose={closeSettings} user={activeUser} token={token} />
      )}
      {settingsView === 'help' && (
        <HelpPage onClose={closeSettings} />
      )}
      {settingsView === 'console' && (
        (activeUser?.role === 'alcedo_operator' && isAlcedoConsoleDomain())
          ? <PlatformConsolePage onClose={closeSettings} token={token} user={activeUser} />
          : <ConsolePage onClose={closeSettings} token={token} user={activeUser} />
      )}
      {settingsView === 'shares' && (
        <ShareManagement onClose={closeSettings} token={token} />
      )}
      {settingsView === 'members' && (
        <MemberManagement onClose={closeSettings} />
      )}
      {settingsView === 'documents' && (
        <DocumentManager onClose={closeSettings} token={token} />
      )}
      {settingsView === 'wallet' && (
        <WalletPage onClose={closeSettings} />
      )}
      {settingsView === 'brain-dump' && (
        <BrainDumpPage onClose={closeSettings} />
      )}
      {settingsView === 'storage' && (
        <StorageUsagePage onClose={closeSettings} />
      )}
      {settingsView === 'notifications' && (
        <NotificationPreferencesView onClose={closeSettings} isFeatureEnabled={isFeatureEnabled} />
      )}

      {/* Context menu overlay (hamburger) */}
      <ContextMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={activeUser}
        theme={theme}
        onSetTheme={setTheme}
        onResetToSystem={resetToSystem}
        onNewSession={newSession}
        onLanguageChange={handleLanguageChange}
        onOpenSettings={(view) => { setSettingsView(view); setMenuOpen(false); }}
      />

      {/* Notification panel */}
      {showNotifications && (
        <NotificationPanel
          token={token}
          onClose={() => { setShowNotifications(false); fetchUnreadCount(); }}
          onNavigate={(detailType, params) => { setShowNotifications(false); openDetail(detailType, params); }}
        />
      )}

      {upgradePrompt && (
        <UpsellModal
          token={token}
          limitType={upgradePrompt.limitType}
          usage={upgradePrompt.usage}
          onClose={() => setUpgradePrompt(null)}
        />
      )}

      {/* A9-10: Global UpsellModal listens for window aif:upsell-needed events */}
      <UpsellModal />

      <InstallBanner canInstall={canInstall} promptInstall={promptInstall} />
      <CookieConsent token={token} />
    </>
    </InsufficientCreditsProvider>
    </NavigationProvider>
    </ErrorBoundary>
  );
}

// ── Help Page ─────────────────────────────────────────────────────────────────
function HelpPage({ onClose }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('menu.help')}</span>
      </header>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-6 py-6 space-y-6">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('help.title')}</h2>
            <p className="text-sm text-[var(--aif-text-secondary)] leading-relaxed">{t('help.description')}</p>
          </div>
          <div className="space-y-2">
            <HelpItem icon="?" label={t('help.faq')} hint={t('help.faqHint')} />
            <HelpItem icon="@" label={t('help.contact')} hint="support@airfarm.io" />
            <HelpItem icon="i" label={t('help.version')} hint="v1.0.0" />
          </div>
        </div>
      </div>
    </div>
  );
}

function HelpItem({ icon, label, hint }) {
  return (
    <div className="flex items-center gap-4 px-4 py-3.5 bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl">
      <div className="w-9 h-9 bg-[var(--aif-bg-elevated)] rounded-lg flex items-center justify-center text-sm font-bold text-[var(--aif-text-muted)]">{icon}</div>
      <div className="flex-1">
        <div className="text-sm text-[var(--aif-text-primary)]">{label}</div>
        <div className="text-xs text-[var(--aif-text-muted)]">{hint}</div>
      </div>
    </div>
  );
}

// ── Notification Panel ────────────────────────────────────────────────────────
function NotificationPanel({ token, onClose, onNavigate }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/api/v1/aif/notifications')
      .then(d => { if (d) setNotifications(d.notifications || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await apiClient.patch(`/api/v1/aif/notifications/${id}/read`, {}, { entityType: 'notification', queueOnOffline: true }).catch(() => {});
    setNotifications(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    await apiClient.post('/api/v1/aif/notifications/read-all', {}, { entityType: 'notification', queueOnOffline: true }).catch(() => {});
    setNotifications(ns => ns.map(n => ({ ...n, read: true })));
  };

  const dismiss = async (id) => {
    await apiClient.delete(`/api/v1/aif/notifications/${id}`, { entityType: 'notification', queueOnOffline: true }).catch(() => {});
    setNotifications(ns => ns.filter(n => n.id !== id));
  };

  const TYPE_ICONS = {
    CONTRACT_EXPIRY: '📋', INSURANCE_EXPIRY: '📋', SUBSIDY_DEADLINE: '💰',
    WEATHER_ALERT: '🌤️', ACHIEVEMENT: '🏆', CASHFLOW_GAP: '💰',
    MARKET_ALERT: '📈', default: 'ℹ️',
  };

  const SEVERITY_BORDER = {
    info: 'border-l-blue-400', warning: 'border-l-orange-400',
    urgent: 'border-l-red-500', success: 'border-l-green-400',
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return t('notification.justNow');
    if (diff < 3600) return t('notification.minutesAgo', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('notification.hoursAgo', { count: Math.floor(diff / 3600) });
    return t('notification.daysAgo', { count: Math.floor(diff / 86400) });
  };

  const unreadCnt = notifications.filter(n => !n.read).length;

  return (
    <div className="fixed inset-0 z-[var(--aif-z-modal)] flex" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-sm bg-[var(--aif-bg-page)] flex flex-col shadow-2xl animate-slideInRight">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('app.notificationTitle')}</span>
            {unreadCnt > 0 && (
              <span className="text-[10px] bg-[var(--aif-color-primary)] text-white rounded-full px-1.5 py-0.5 font-bold">{unreadCnt}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCnt > 0 && (
              <button onClick={markAllRead} className="text-xs text-[var(--aif-color-primary)] hover:opacity-75">{t('app.notificationMarkAll')}</button>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-surface)]">✕</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <div className="text-4xl mb-3">🔔</div>
              <p className="text-sm text-[var(--aif-text-muted)]">{t('app.notificationNone')}</p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--aif-border-default)]">
              {notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => {
                    if (!n.read) markRead(n.id);
                    if (onNavigate) {
                      // Deep link: parse action_url for entity-specific navigation
                      const url = n.action_url || '';
                      const NAV_MAP = {
                        CONTRACT_EXPIRY: 'contracts',
                        INSURANCE_EXPIRY: 'contracts',
                        SUBSIDY_DEADLINE: 'subsidies',
                        WEATHER_ALERT: 'weather',
                        CASHFLOW_GAP: 'cashflow',
                        MARKET_ALERT: 'market',
                        LOW_STOCK: 'storage',
                        TASK_OVERDUE: 'weekly',
                        APPROVAL_NEEDED: 'weekly',
                      };
                      const target = NAV_MAP[n.type];
                      const params = {};
                      if (n.related_entity_id) params.entityId = n.related_entity_id;
                      if (target) onNavigate(target, params);
                    }
                  }}
                  className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors border-l-4 ${SEVERITY_BORDER[n.severity] || SEVERITY_BORDER.info} ${n.read ? 'bg-[var(--aif-bg-page)] opacity-70' : 'bg-[var(--aif-bg-surface)] hover:bg-[var(--aif-bg-elevated)]'}`}
                >
                  <div className="text-xl flex-shrink-0 mt-0.5">{TYPE_ICONS[n.type] || TYPE_ICONS.default}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--aif-text-primary)] leading-snug">{n.title}</div>
                    <div className="text-xs text-[var(--aif-text-muted)] mt-0.5 line-clamp-2">{n.message}</div>
                    <div className="text-[10px] text-[var(--aif-text-muted)] mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-[var(--aif-text-muted)] hover:text-red-400 text-xs"
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
